/**
 * Manual Demand Config — fallback pump-rate input for the Theis tabs.
 *
 * When a site has no gauging history to reverse-engineer per-well Q from, the
 * user can enter a use case + quantity + well count here. The total demand is
 * split evenly across the wells to give a per-well GPM that drives Cone of
 * Depression and Sustainable Yield. Shared between both tabs so they always
 * agree on the manual rate.
 *
 * This is a fallback only — wells with real gauging (or a manual asset
 * pump_rate_gpm) always take precedence (see deriveWellGpm).
 */
import React, { useState, useEffect } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import {
  USE_CASE_DEMAND,
  manualDemandAfYr,
  manualPerWellGpm,
  afYrToGpm,
  type ManualDemand,
} from '@/lib/wellGpm';

interface Props {
  config: ManualDemand;
  onChange: (next: ManualDemand) => void;
}

export function ManualDemandConfig({ config, onChange }: Props) {
  // Local draft — edits stay here until Save commits them to the shared state
  // (which feeds both Cone of Depression and Sustainable Yield). Re-syncs if
  // the committed config changes elsewhere (e.g. saved from the other tab).
  const [draft, setDraft] = useState<ManualDemand>(config);
  useEffect(() => { setDraft(config); }, [config.useCase, config.quantity, config.numWells]);

  const dirty = draft.useCase !== config.useCase
    || draft.quantity !== config.quantity
    || draft.numWells !== config.numWells;

  const info = USE_CASE_DEMAND[draft.useCase];
  const totalAf = manualDemandAfYr(draft);
  const perWell = manualPerWellGpm(draft);
  const totalGpm = afYrToGpm(totalAf);

  const groups = Array.from(new Set(Object.values(USE_CASE_DEMAND).map(u => u.group)));

  return (
    <div className="bg-mono-800/40 border border-mono-800 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] text-mono-500 uppercase tracking-wider">
          Manual Demand — no gauging fallback
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-[10px] text-cyan-400">Unsaved</span>}
          <button
            onClick={() => onChange(draft)}
            disabled={!dirty}
            className="flex items-center gap-1 px-3 py-1 text-xs rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 disabled:opacity-40 disabled:cursor-default"
          >
            <CheckIcon className="w-3 h-3" />
            {dirty ? 'Save' : 'Saved'}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="text-[10px] text-mono-500 uppercase tracking-wider">Use Case</label>
          <select
            value={draft.useCase}
            onChange={e => setDraft({ ...draft, useCase: e.target.value })}
            className="w-full mt-0.5 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200 focus:border-cyan-500 focus:outline-none"
          >
            <option value="" disabled>Select use case...</option>
            {groups.map(g => (
              <optgroup key={g} label={g}>
                {Object.entries(USE_CASE_DEMAND)
                  .filter(([, u]) => u.group === g)
                  .map(([key, u]) => <option key={key} value={key}>{u.label}</option>)}
              </optgroup>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] text-mono-500 uppercase tracking-wider">
            Quantity {info ? `(${info.unit})` : ''}
          </label>
          <input
            type="number" min={0} value={draft.quantity || ''}
            onChange={e => setDraft({ ...draft, quantity: Number(e.target.value) })}
            className="w-full mt-0.5 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200 focus:border-cyan-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="text-[10px] text-mono-500 uppercase tracking-wider"># Wells</label>
          <input
            type="number" min={1} value={draft.numWells || ''}
            onChange={e => setDraft({ ...draft, numWells: Number(e.target.value) })}
            className="w-full mt-0.5 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200 focus:border-cyan-500 focus:outline-none"
          />
        </div>
        <div className="bg-mono-800/50 rounded p-1.5">
          <div className="text-[9px] text-mono-500 uppercase">Per-Well Rate</div>
          <div className="text-xs text-cyan-400 font-semibold">{perWell.toFixed(0)} GPM</div>
          <div className="text-[9px] text-mono-400">{Math.round(totalAf).toLocaleString()} AF/yr · {totalGpm.toFixed(0)} GPM total</div>
        </div>
      </div>
      <div className="text-[10px] text-mono-500 mt-2">
        Used only for wells without gauging or a manual pump rate. Total demand ÷ wells = per-well GPM → Theis.
        {dirty && <span className="text-cyan-400"> Save to apply to Cone of Depression + Sustainable Yield.</span>}
      </div>
    </div>
  );
}
